import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Loader2, RefreshCcw, Scissors, X } from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney, fmtDateTime } from "../lib/format";
import StripePayment from "./StripePayment";

// Long-stay selection experience (§4): intervals beyond the 30-day monthly
// maximum surface ONLY the host-configured alternatives — a composite
// monthly+remainder booking, the existing auto-renew subscription (explicit
// recurring-billing consent required), or shortening to the 30-day maximum.
// All prices and dates come from the server quote verbatim.
export default function LongStayOptions({ options, currency, spotId, monthlyPlusSelected, onMonthlyPlus, onShorten }) {
  const [renewOpen, setRenewOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sub, setSub] = useState(null);
  const navigate = useNavigate();
  if (!options) return null;
  const { auto_renew: ar, monthly_plus: mp } = options;

  const subscribe = async () => {
    setBusy(true);
    try { setSub(await api.subscribe(spotId)); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-2xl border border-[#FF3B45]/25 bg-[#E50914]/5 p-4" data-testid="long-stay-options">
      <p className="meta-label !text-[#FF3B45]">Staying longer than 30 days?</p>
      <p className="mt-1.5 text-xs text-zinc-400 leading-relaxed">
        A single monthly booking covers up to 30 days. For your selected period, choose one of the options this host supports:
      </p>
      <div className="mt-3 space-y-2">
        {mp?.available ? (
          <button type="button" onClick={onMonthlyPlus} data-testid="long-stay-monthly-plus"
                  className={`w-full text-left rounded-xl border px-4 py-3.5 transition-all duration-200 ${monthlyPlusSelected ? "border-[#E50914] bg-[#E50914]/10 shadow-lg shadow-[#E50914]/10" : "border-[#27272A] bg-[#0A0D10] hover:border-[#3F3F46]"}`}>
            <span className="flex items-center justify-between gap-3">
              <span className="text-sm font-bold text-white flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#FF3B45]" /> One booking — monthly + remainder</span>
              <span className="font-display font-bold text-white" data-testid="long-stay-monthly-plus-price">{fmtMoney(mp.parking_cents / 100, currency)}</span>
            </span>
            {mp.periods?.map((p, i) => (
              <span key={i} className="block mt-1 text-[11px] text-zinc-500" data-testid={`long-stay-period-${i}`}>· {p.label}: {fmtMoney(p.parking_cents / 100, currency)}</span>
            ))}
          </button>
        ) : (
          <div className="rounded-xl border border-[#1F2328] bg-[#0A0D10]/60 px-4 py-3 opacity-60" data-testid="long-stay-monthly-plus-disabled">
            <span className="text-sm font-semibold text-zinc-400 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> One booking — monthly + remainder</span>
            <span className="block mt-1 text-[11px] text-amber-400/90">{mp?.reason || "Not available for this listing."}</span>
          </div>
        )}
        {ar?.available ? (
          <button type="button" onClick={() => { setRenewOpen(true); setAck(false); }} data-testid="long-stay-auto-renew"
                  className="w-full text-left rounded-xl border border-[#27272A] bg-[#0A0D10] px-4 py-3.5 hover:border-[#3F3F46] transition-colors">
            <span className="flex items-center justify-between gap-3">
              <span className="text-sm font-bold text-white flex items-center gap-2"><RefreshCcw className="w-4 h-4 text-[#FF3B45]" /> Monthly auto-renewal</span>
              <span className="font-display font-bold text-white">{fmtMoney(ar.monthly_cents / 100, currency)}/mo</span>
            </span>
            <span className="block mt-1 text-[11px] text-zinc-500">
              First period ({ar.initial_days} days) {fmtMoney(ar.monthly_cents / 100, currency)} · renews {fmtDateTime(ar.renewal_date)} · cancel anytime
            </span>
          </button>
        ) : (
          <div className="rounded-xl border border-[#1F2328] bg-[#0A0D10]/60 px-4 py-3 opacity-60" data-testid="long-stay-auto-renew-disabled">
            <span className="text-sm font-semibold text-zinc-400 flex items-center gap-2"><RefreshCcw className="w-4 h-4" /> Monthly auto-renewal</span>
            <span className="block mt-1 text-[11px] text-amber-400/90">{ar?.reason || "Not available for this listing."}</span>
          </div>
        )}
        <button type="button" onClick={onShorten} data-testid="long-stay-shorten"
                className="w-full text-left rounded-xl border border-[#27272A] bg-[#0A0D10] px-4 py-3 hover:border-[#3F3F46] transition-colors">
          <span className="text-sm font-semibold text-zinc-200 flex items-center gap-2"><Scissors className="w-4 h-4 text-[#FF3B45]" /> Shorten to 30 days</span>
          <span className="block mt-1 text-[11px] text-zinc-500">Book the maximum single monthly period instead.</span>
        </button>
      </div>

      {renewOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
             role="dialog" aria-modal="true" aria-label="Monthly auto-renewal agreement" data-testid="renew-consent-modal">
          <div className="w-full max-w-lg rounded-2xl border border-[#27272A] bg-[#0D1114] p-6 sm:p-7 shadow-2xl shadow-black/60 max-h-[85dvh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="meta-label !text-[#FF3B45]">Recurring billing agreement</p>
                <h3 className="type-h3 mt-1.5">Monthly auto-renewal</h3>
              </div>
              <button type="button" onClick={() => { setRenewOpen(false); setSub(null); }} aria-label="Close" data-testid="renew-consent-close"
                      className="text-zinc-500 hover:text-white transition-colors shrink-0"><X className="w-5 h-5" /></button>
            </div>
            {!sub ? (
              <>
                <div className="mt-4 rounded-xl border border-[#1F2328] bg-[#080B0D] p-4 space-y-2 text-sm text-zinc-300" data-testid="renew-terms">
                  <p className="flex justify-between"><span>First period ({ar.initial_days} days)</span><span className="font-semibold text-white">{fmtMoney(ar.monthly_cents / 100, currency)}</span></p>
                  <p className="flex justify-between"><span>Renews on</span><span className="font-semibold text-white">{fmtDateTime(ar.renewal_date)}</span></p>
                  <p className="flex justify-between"><span>Renewal price</span><span className="font-semibold text-white">{fmtMoney(ar.monthly_cents / 100, currency)}/month</span></p>
                  <p className="text-xs text-zinc-500 leading-relaxed pt-1">{ar.note}</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">You can cancel anytime from your account — cancelling stops future renewals. Applicable fees and taxes are shown before each charge where required.</p>
                </div>
                <label className="mt-4 flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                         data-testid="renew-consent-checkbox" className="mt-0.5 w-4 h-4 accent-[#E50914]" />
                  <span className="text-sm text-zinc-200">I authorize MyRedPark to charge my saved payment method {fmtMoney(ar.monthly_cents / 100, currency)} every month, starting {fmtDateTime(ar.renewal_date)}, until I cancel.</span>
                </label>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setRenewOpen(false)} disabled={busy} data-testid="renew-consent-decline" className="btn-secondary !h-11 text-sm">Cancel</button>
                  <button type="button" onClick={subscribe} disabled={!ack || busy} data-testid="renew-consent-accept"
                          className="btn-primary !h-11 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Agree &amp; continue
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-5" data-testid="renew-payment">
                <p className="text-sm text-zinc-300 mb-4">Authorize your first month — future renewals bill automatically until you cancel.</p>
                <StripePayment clientSecret={sub.client_secret} publishableKey={sub.publishable_key}
                               amountLabel={`${fmtMoney(sub.monthly_amount, currency)}/mo`}
                               onSuccess={() => { toast.success("Monthly parking started — renews monthly until you cancel."); navigate("/bookings"); }}
                               onError={(m) => toast.error(m)} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
